import { useCallback, useState, useEffect } from "react";

const FORM_DATA_STORAGE_KEY = "registrationFormData";

interface FormData {
  userPortalEmail: string;
  userName: string;
  email: string;
  password?: string;
  region: string;
  companyName: string;
  vat: string;
  phone: string;
  registration: string;
  zip: string;
  city: string;
  address: string;
  owners: string[];
  boards: string[];
}

interface UseAuthReturnValues {
  userPortalEmail: string;
  userName: string;
  email: string;
  password: string;
  region?: string;
  companyName?: string;
  vat?: string;
  phone?: string;
  registration?: string;
  zip?: string;
  city?: string;
  address?: string;
  owners: string[];
  boards: string[];
  handleChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeZip: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeAddress: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeOwner: (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleAddOwner: VoidFunction;
  handleAddBoard: VoidFunction;
  handleRemoveOwner: (index: number) => void;
  handleRemoveBoard: (index: number) => void;
  handleChangeBoard: (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleChangeCity: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangePassword: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeRegion: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleChangeCompanyName: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeUserName: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeUserPortalEmail: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleChangeVat: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangePhone: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleChangeRegistration: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleAuth: VoidFunction;
  saveFormData: () => void;
  loadFormData: () => FormData | null;
  clearFormData: () => void;
  setFormDataFromServer: (data: Partial<FormData>) => void;
}

interface UseAuthProps {
  handleClick: (
    userPortalEmail: string,
    userName: string,
    email: string,
    password: string,
    region: string,
    companyName: string,
    vat: string,
    phone: string,
    registration: string,
    zip: string,
    city: string,
    address: string,
    owners: string[],
    boards: string[]
  ) => void;
}

const loadFormDataFromStorage = (): FormData | null => {
  try {
    const saved = localStorage.getItem(FORM_DATA_STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const saveFormDataToStorage = (data: FormData) => {
  try {
    localStorage.setItem(FORM_DATA_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save form data:", error);
  }
};

const clearFormDataFromStorage = () => {
  try {
    localStorage.removeItem(FORM_DATA_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear form data:", error);
  }
};

export function useAuth({ handleClick }: UseAuthProps): UseAuthReturnValues {
  const [email, setEmail] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [boards, setBoards] = useState<string[]>([""]);
  const [owners, setOwners] = useState<string[]>([""]);
  const [password, setPassword] = useState<string>("");
  const [region, setRegion] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");
  const [userPortalEmail, setUserPortalEmail] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [vat, setVat] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [registration, setRegistration] = useState<string>("");

  useEffect(() => {
    const saved = loadFormDataFromStorage();
    if (saved) {
      setEmail(saved.email || "");
      setZip(saved.zip || "");
      setCity(saved.city || "");
      setAddress(saved.address || "");
      setBoards(saved.boards && saved.boards.length > 0 ? saved.boards : [""]);
      setOwners(saved.owners && saved.owners.length > 0 ? saved.owners : [""]);
      setRegion(saved.region || "");
      setCompanyName(saved.companyName || "");
      setUserPortalEmail(saved.userPortalEmail || "");
      setUserName(saved.userName || "");
      setVat(saved.vat || "");
      setPhone(saved.phone || "");
      setRegistration(saved.registration || "");
    }
  }, []);

  useEffect(() => {
    const formData: FormData = {
      userPortalEmail,
      userName,
      email,
      region,
      companyName,
      vat,
      phone,
      registration,
      zip,
      city,
      address,
      owners,
      boards,
    };
    saveFormDataToStorage(formData);
  }, [
    userPortalEmail,
    userName,
    email,
    region,
    companyName,
    vat,
    phone,
    registration,
    zip,
    city,
    address,
    owners,
    boards,
  ]);

  const handleChangeUserPortalEmail = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUserPortalEmail(event.target.value?.trim() || "");
    },
    []
  );

  const handleChangeUserName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUserName(event.target.value);
    },
    []
  );

  const handleChangeZip = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setZip(event.target.value);
    },
    []
  );
  const handleChangeAddress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddress(event.target.value);
    },
    []
  );

  const handleChangeCity = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCity(event.target.value);
    },
    []
  );

  const handleChangeOwner = useCallback(
    (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
      setOwners(
        owners.map((owner, i) => (i === index ? event.target.value : owner))
      );
    },
    [owners]
  );

  const handleAddOwner = useCallback(() => {
    setOwners([...owners, ""]);
  }, [owners]);

  const handleRemoveOwner = useCallback(
    (index: number) => {
      setOwners(owners.filter((_, i) => i !== index));
    },
    [owners]
  );

  const handleChangeBoard = useCallback(
    (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
      setBoards(
        boards.map((board, i) => (i === index ? event.target.value : board))
      );
    },
    [boards]
  );

  const handleRemoveBoard = useCallback(
    (index: number) => {
      setBoards(boards.filter((_, i) => i !== index));
    },
    [boards]
  );

  const handleAddBoard = useCallback(() => {
    setBoards([...boards, ""]);
  }, [boards]);

  const handleChangeEmail = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value?.trim() || "");
    },
    []
  );

  const handleChangePassword = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value?.trim() || "");
    },
    []
  );

  const handleChangeRegion = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setRegion(event.target.value);
    },
    []
  );

  const handleChangeCompanyName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCompanyName(event.target.value);
    },
    []
  );

  const handleChangeVat = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setVat(event.target.value);
    },
    []
  );

  const handleChangePhone = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPhone(event.target.value);
    },
    []
  );

  const handleChangeRegistration = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRegistration(event.target.value);
    },
    []
  );

  const handleAuth = useCallback(() => {
    handleClick(
      userPortalEmail,
      userName,
      email,
      password,
      region,
      companyName,
      vat,
      phone,
      registration,
      zip,
      city,
      address,
      owners,
      boards
    );
  }, [
    userPortalEmail,
    userName,
    email,
    password,
    region,
    companyName,
    vat,
    phone,
    registration,
    zip,
    city,
    address,
    owners,
    boards,
    handleClick,
  ]);

  const saveFormData = useCallback(() => {
    const formData: FormData = {
      userPortalEmail,
      userName,
      email,
      password,
      region,
      companyName,
      vat,
      phone,
      registration,
      zip,
      city,
      address,
      owners,
      boards,
    };
    saveFormDataToStorage(formData);
  }, [
    userPortalEmail,
    userName,
    email,
    password,
    region,
    companyName,
    vat,
    phone,
    registration,
    zip,
    city,
    address,
    owners,
    boards,
  ]);

  const loadFormData = useCallback((): FormData | null => {
    return loadFormDataFromStorage();
  }, []);

  const clearFormData = useCallback(() => {
    clearFormDataFromStorage();
    setEmail("");
    setZip("");
    setCity("");
    setAddress("");
    setBoards([""]);
    setOwners([""]);
    setPassword("");
    setRegion("");
    setCompanyName("");
    setUserPortalEmail("");
    setUserName("");
    setVat("");
    setPhone("");
    setRegistration("");
  }, []);

  const setFormDataFromServer = useCallback((data: Partial<FormData>) => {
    if (data.email !== undefined) setEmail(data.email);
    if (data.zip !== undefined) setZip(data.zip);
    if (data.city !== undefined) setCity(data.city);
    if (data.address !== undefined) setAddress(data.address);
    if (data.boards !== undefined)
      setBoards(data.boards.length > 0 ? data.boards : [""]);
    if (data.owners !== undefined)
      setOwners(data.owners.length > 0 ? data.owners : [""]);
    if (data.password !== undefined) setPassword(data.password);
    if (data.region !== undefined) setRegion(data.region);
    if (data.companyName !== undefined) setCompanyName(data.companyName);
    if (data.userPortalEmail !== undefined)
      setUserPortalEmail(data.userPortalEmail);
    if (data.userName !== undefined) setUserName(data.userName);
    if (data.vat !== undefined) setVat(data.vat);
    if (data.phone !== undefined) setPhone(data.phone);
    if (data.registration !== undefined) setRegistration(data.registration);
  }, []);

  return {
    userPortalEmail,
    userName,
    email,
    password,
    region,
    companyName,
    vat,
    phone,
    registration,
    zip,
    city,
    address,
    owners,
    boards,
    handleChangeEmail,
    handleChangeZip,
    handleChangeCity,
    handleChangeAddress,
    handleChangeOwner,
    handleAddOwner,
    handleRemoveOwner,
    handleChangeBoard,
    handleAddBoard,
    handleRemoveBoard,
    handleChangePassword,
    handleChangeRegion,
    handleChangeCompanyName,
    handleChangeUserPortalEmail,
    handleChangeUserName,
    handleChangeVat,
    handleChangePhone,
    handleChangeRegistration,
    handleAuth,
    saveFormData,
    loadFormData,
    clearFormData,
    setFormDataFromServer,
  };
}
